import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Container, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { APP, formatDateTime, type HealthResponse } from '@parts/shared';
import { getJson } from '../../api';
import { t, tx, useLang } from '../../i18n';
import classes from './StatusPage.module.css';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; data: HealthResponse; checkedAt: Date }
  | { kind: 'error'; message: string; checkedAt: Date };

/** Страница проверки: сервер и база данных (адрес /status). */
export function StatusPage() {
  useLang();
  const [state, setState] = useState<State>({ kind: 'loading' });

  const check = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const data = await getJson<HealthResponse>('/api/health', { allowStatus: [503] });
      setState({ kind: 'ready', data, checkedAt: new Date() });
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err), checkedAt: new Date() });
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  const allOk = state.kind === 'ready' && state.data.database.ok;

  return (
    <Box className={classes.page}>
      <Container size={560} px="md" py={48}>
        <Stack gap={28}>
          <div>
            <Title order={1} className={classes.title}>
              {t(APP.NAME)}
            </Title>
            <Text c="dimmed" mt={6}>
              {t('Версия {v}. Проверка сервера и базы данных.', { v: APP.VERSION })} <a href="/">{t('К приложению')}</a>
            </Text>
          </div>

          <Paper withBorder p={0} className={classes.panel}>
            <div className={classes.verdict} data-state={state.kind === 'loading' ? 'loading' : allOk ? 'ok' : 'fail'}>
              {state.kind === 'loading' && t('Проверяю…')}
              {state.kind !== 'loading' && (allOk ? t('Всё работает') : t('Есть проблема'))}
            </div>

            <dl className={classes.rows}>
              <CheckRow
                label={t('Сервер')}
                ok={state.kind === 'ready'}
                pending={state.kind === 'loading'}
                value={
                  state.kind === 'ready'
                    ? t('работает, {time}', { time: formatUptime(state.data.uptimeSeconds) })
                    : state.kind === 'error'
                      ? tx(state.message)
                      : ''
                }
              />
              <CheckRow
                label={t('База данных')}
                ok={state.kind === 'ready' && state.data.database.ok}
                pending={state.kind === 'loading'}
                value={dbText(state)}
              />
              {state.kind === 'ready' && state.data.database.ok && (
                <>
                  <InfoRow
                    label={state.data.database.kind === 'embedded' ? t('Папка базы') : t('Сервер базы')}
                    value={state.data.database.location}
                  />
                  <InfoRow label={t('Миграции')} value={String(state.data.database.migrations)} />
                  <InfoRow
                    label={t('База создана')}
                    value={state.data.database.createdAt ? formatDateTime(state.data.database.createdAt) : '—'}
                  />
                </>
              )}
              {state.kind === 'ready' && (
                <InfoRow label={t('Время сервера')} value={`${formatDateTime(state.data.serverTime)} (${t('Москва')})`} />
              )}
            </dl>
          </Paper>

          {state.kind === 'error' && (
            <Alert color="red" variant="light" title={t('Что проверить')}>
              {t('На ПК: открыто ли окно консоли, которое запустил start.bat (если закрыто — запустите снова; если открыто — пришлите последние строки из него). На сервере: выполните sudo partsctl status и sudo partsctl logs.')}
            </Alert>
          )}

          <Group justify="space-between" align="center">
            <Text size="sm" c="dimmed">
              {state.kind !== 'loading' && t('Проверено в {time}', { time: formatDateTime(state.checkedAt).split(' ')[1] ?? '' })}
            </Text>
            <Button onClick={() => void check()} loading={state.kind === 'loading'} variant="default">
              {t('Проверить ещё раз')}
            </Button>
          </Group>
        </Stack>
      </Container>
    </Box>
  );
}

function dbText(state: State): string {
  if (state.kind !== 'ready') return '';
  const db = state.data.database;
  if (!db.ok) return db.error;
  const kind = db.kind === 'embedded' ? t('встроенная') : t('сервер');
  return t('подключена ({kind}), PostgreSQL {v}, отклик {ms} мс', { kind, v: db.version, ms: db.latencyMs });
}

function CheckRow(props: { label: string; ok: boolean; pending: boolean; value: string }) {
  const status = props.pending ? 'pending' : props.ok ? 'ok' : 'fail';
  return (
    <div className={classes.row}>
      <dt>
        <span className={classes.dot} data-status={status} aria-hidden />
        {props.label}
      </dt>
      <dd>{props.pending ? '…' : props.value}</dd>
    </div>
  );
}

function InfoRow(props: { label: string; value: string }) {
  return (
    <div className={classes.row}>
      <dt className={classes.infoLabel}>{props.label}</dt>
      <dd>{props.value}</dd>
    </div>
  );
}

function formatUptime(sec: number): string {
  if (sec < 60) return t('{n} с', { n: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return t('{n} мин', { n: min });
  const h = Math.floor(min / 60);
  return t('{h} ч {m} мин', { h, m: min % 60 });
}
